
import { useState } from "react";
import { useStudentBilling, useBillingPeriods, useActivityPrices, generateBillingForPeriod } from "@/hooks/useFinanceData";
import { FinancesHeader } from "./finances/FinancesHeader";
import { InvoiceGenerator } from "./finances/InvoiceGenerator";
import { FinancialCharts } from "./finances/FinancialCharts";
import { FinancesMetrics } from "./finances/FinancesMetrics";
import { ActivityPricesCard } from "./finances/ActivityPricesCard";
import { ActivityPriceManager } from "./finances/ActivityPriceManager";
import { BillingPeriodsCard } from "./finances/BillingPeriodsCard";
import { BillingTypeStatsCard } from "./finances/BillingTypeStatsCard";
import { FinancesTableContainer } from "./finances/FinancesTableContainer";
import { DebtorsTable } from "./finances/DebtorsTable";
import { PaymentRegistration } from "./finances/PaymentRegistration";
import { FinancialReports } from "./finances/FinancialReports";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Euro, Settings } from "lucide-react";
import { toast } from "sonner";

export const FinancesDashboard = () => {
  const { billing: originalBilling, loading: billingLoading, refetch: refetchBilling } = useStudentBilling();
  const { periods, refetch: refetchPeriods } = useBillingPeriods();
  const { prices, refetch: refetchPrices } = useActivityPrices();
  const [isGenerating, setIsGenerating] = useState(false);

  // Obtener período activo
  const activePeriod = periods.find(p => p.status === 'active');

  const handleGenerateInvoices = async () => {
    if (!activePeriod) {
      toast.error("No hay período de facturación activo. Activa un período primero.");
      return;
    }

    try {
      setIsGenerating(true);
      const result = await generateBillingForPeriod(
        activePeriod.id,
        activePeriod.start_date,
        activePeriod.end_date
      );

      if (result.success) {
        toast.success(
          `✅ Facturación completada!\n${result.message}`,
          {
            description: `💰 Total generado: ${result.stats.totalAmount.toFixed(2)}€`,
            duration: 5000
          }
        );

        // Mostrar detalles si hay estadísticas interesantes
        if (result.stats.skipped > 0 || result.stats.errors > 0) {
          console.log("📋 Detalles de la generación:", result.stats.details);
          
          if (result.stats.errors > 0) {
            const errorDetails = result.stats.details.filter((d: any) => d.status === 'error');
            toast.error(
              `⚠️ Se encontraron ${result.stats.errors} errores`,
              {
                description: `Revisa la consola para más detalles. Errores comunes: actividades sin precio configurado.`,
                duration: 8000
              }
            );
          }
        }
      } else {
        toast.error(`❌ Error en la facturación: ${result.message}`);
      }
      
      refetchBilling();
    } catch (error: any) {
      console.error('Error generating invoices:', error);
      toast.error("Error al generar facturas: " + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRefresh = () => {
    refetchBilling();
    refetchPeriods();
    refetchPrices();
  };

  return (
    <div className="space-y-6">
      <FinancesHeader />
      
      <InvoiceGenerator 
        activePeriod={activePeriod}
        isGenerating={isGenerating}
        onGenerateInvoices={handleGenerateInvoices}
      />

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="reports">Reportes</TabsTrigger>
          <TabsTrigger value="debtors">Deudores</TabsTrigger>
          <TabsTrigger value="invoices">Facturas</TabsTrigger>
          <TabsTrigger value="config">Configuración</TabsTrigger>
        </TabsList>
        
        <TabsContent value="dashboard" className="mt-6 space-y-6">
          <FinancesMetrics billing={originalBilling} />
          
          <div className="mt-6">
            <FinancialCharts />
          </div>
        </TabsContent>
        
        <TabsContent value="reports" className="mt-6">
          <FinancialReports />
        </TabsContent>
        
        <TabsContent value="debtors" className="mt-6">
          <DebtorsTable onRefresh={handleRefresh} />
        </TabsContent>
        
        <TabsContent value="invoices" className="mt-6">
          <FinancesTableContainer />
        </TabsContent>
        
        <TabsContent value="config" className="mt-6">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary" />
                Configuración Financiera
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-6">
                Configura los precios por actividad, períodos de facturación y otros parámetros del sistema financiero.
              </p>
              
              <div className="space-y-6">
                <ActivityPriceManager 
                  prices={prices || []} 
                  onPriceCreated={handleRefresh}
                />
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <ActivityPricesCard prices={prices || []} />
                  <BillingPeriodsCard periods={periods} onRefetch={refetchPeriods} />
                  <BillingTypeStatsCard />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
